/**
 * Article content renderer with basic markdown support
 * Renders help article content with proper styling
 *
 * For full markdown support, install react-markdown:
 * npm install react-markdown remark-gfm
 */

import { useMemo } from 'react';

interface HelpArticleContentProps {
  content: string;
  className?: string;
}

/**
 * Basic markdown parser for common elements
 * Supports: headers, bold, italic, links, lists, code blocks
 */
function parseMarkdown(content: string): string {
  let html = content;

  // Escape HTML first (security)
  html = html
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Code blocks (```...```)
  html = html.replace(
    /```(\w*)\n([\s\S]*?)```/g,
    '<pre class="bg-gray-100 dark:bg-gray-900 rounded-lg p-4 overflow-x-auto my-4"><code class="text-sm">$2</code></pre>'
  );

  // Inline code (`...`)
  html = html.replace(
    /`([^`]+)`/g,
    '<code class="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-900 rounded text-sm">$1</code>'
  );

  // Headers
  html = html.replace(
    /^### (.+)$/gm,
    '<h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-6 mb-3">$1</h3>'
  );
  html = html.replace(
    /^## (.+)$/gm,
    '<h2 class="text-xl font-semibold text-gray-900 dark:text-gray-100 mt-8 mb-4">$1</h2>'
  );
  html = html.replace(
    /^# (.+)$/gm,
    '<h1 class="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-8 mb-4">$1</h1>'
  );

  // Bold
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold">$1</strong>');

  // Italic
  html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');

  // Links
  html = html.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    '<a href="$2" class="text-primary-600 dark:text-primary-400 hover:underline" target="_blank" rel="noopener noreferrer">$1</a>'
  );

  // Horizontal rule
  html = html.replace(/^---$/gm, '<hr class="my-8 border-gray-200 dark:border-gray-700" />');

  // Unordered lists
  html = html.replace(
    /^- (.+)$/gm,
    '<li class="ml-4 list-disc list-inside text-gray-700 dark:text-gray-300">$1</li>'
  );

  // Ordered lists
  html = html.replace(
    /^\d+\. (.+)$/gm,
    '<li class="ml-4 list-decimal list-inside text-gray-700 dark:text-gray-300">$1</li>'
  );

  // Blockquotes
  html = html.replace(
    /^> (.+)$/gm,
    '<blockquote class="border-l-4 border-primary-500 pl-4 py-2 my-4 text-gray-600 dark:text-gray-400 italic">$1</blockquote>'
  );

  // Paragraphs (double newlines)
  html = html.replace(/\n\n/g, '</p><p class="text-gray-700 dark:text-gray-300 mb-4">');

  // Single newlines to line breaks
  html = html.replace(/\n/g, '<br />');

  // Wrap in paragraph
  html = `<p class="text-gray-700 dark:text-gray-300 mb-4">${html}</p>`;

  // Clean up empty paragraphs
  html = html.replace(/<p[^>]*><\/p>/g, '');
  html = html.replace(/<p[^>]*><br \/><\/p>/g, '');

  return html;
}

export function HelpArticleContent({
  content,
  className = '',
}: HelpArticleContentProps): JSX.Element {
  const renderedContent = useMemo(() => parseMarkdown(content), [content]);

  return (
    <article
      className={`prose prose-gray dark:prose-invert max-w-none ${className}`}
      dangerouslySetInnerHTML={{ __html: renderedContent }}
    />
  );
}

export default HelpArticleContent;
